import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";
import { calculateBrandCompletion } from "@/lib/brandValidation";
import { debug } from "@/lib/debug";
import type { Brand } from "@/contexts/BrandContext";

/**
 * How complete one brand is, as a percentage.
 *
 * Deliberately a thin wrapper around `calculateBrandCompletion` rather than a second
 * calculation: the Dashboard's COMPLETION column calls that same function with the same two
 * inputs, so any caller of this hook shows the number the member already sees there. Two
 * different percentages for one brand would be worse than showing none at all.
 *
 * The second input is the one that is easy to get wrong. `calculateBrandCompletion` runs an
 * 11-step check when it is told whether the brand has a customer avatar, and falls back to a
 * 5-check legacy version when it is not — and the two disagree. So the avatar lookup is not
 * optional here; skipping it to save a query would produce exactly the mismatch this exists to
 * avoid. It is one row, keyed on the brand, and only re-runs when the brand changes.
 */
export function useBrandCompletion(brand: Brand | null | undefined): number | null {
  const effectiveUserId = useEffectiveUserId();
  const [hasAvatar, setHasAvatar] = useState<boolean | null>(null);

  useEffect(() => {
    if (!brand?.id || !effectiveUserId) {
      setHasAvatar(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("customer_avatars")
      .select("id")
      .eq("user_id", effectiveUserId)
      .eq("brand_id", brand.id)
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          debug.error("[useBrandCompletion] avatar lookup failed:", error.message);
          setHasAvatar(null);
          return;
        }
        setHasAvatar((data ?? []).length > 0);
      });
    return () => {
      cancelled = true;
    };
  }, [brand?.id, effectiveUserId]);

  if (!brand) return null;
  // Null until the lookup lands, so nothing renders a percentage computed from the wrong branch
  // and then visibly corrects itself a moment later.
  if (hasAvatar === null) return null;
  return calculateBrandCompletion(brand, hasAvatar);
}
